import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Send, CheckCircle2, Globe, Loader2, BarChart3, X, KeyRound } from "lucide-react";
import DOMPurify from "dompurify";
import api from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";

export default function Settings() {
  const [sender, setSender] = useState("");
  const [info, setInfo] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [digestLoading, setDigestLoading] = useState(false);
  const [digestHtml, setDigestHtml] = useState("");
  const [digestCfg, setDigestCfg] = useState(null);
  const [savingDigest, setSavingDigest] = useState(false);
  const [newRecipient, setNewRecipient] = useState("");
  const [cp, setCp] = useState({ current: "", next: "", confirm: "" });
  const [cpSaving, setCpSaving] = useState(false);

  useEffect(() => {
    api.get("/settings/email").then((r) => { setInfo(r.data); setSender(r.data.sender_email); }).catch(() => toast.error("Admin access required"));
    api.get("/settings/digest").then((r) => setDigestCfg(r.data)).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put("/settings/email", { sender_email: sender }); setSender(data.sender_email); toast.success("Sender email saved"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  const sendTest = async () => {
    setTesting(true);
    try { const { data } = await api.post("/notifications/test-email"); toast.success(`Test email sent to ${data.sent_to}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not send"); }
    finally { setTesting(false); }
  };

  const usingCustom = info && info.sender_email !== info.default_sender;

  const previewDigest = async () => {
    setDigestLoading(true);
    try {
      const { data } = await api.post("/digest/preview");
      setDigestHtml(data.html || "");
      toast.success(`Test digest sent to ${data.sent_to}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not generate digest"); }
    finally { setDigestLoading(false); }
  };

  const saveDigest = async (next) => {
    setSavingDigest(true);
    try {
      const payload = {
        enabled: next.enabled, weekday: next.weekday,
        recipients: next.recipients ?? (digestCfg?.recipients || []),
      };
      const { data } = await api.put("/settings/digest", payload);
      setDigestCfg(data);
      toast.success("Digest settings saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setSavingDigest(false); }
  };

  const addRecipient = () => {
    const e = newRecipient.trim().toLowerCase();
    if (!e) return;
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e)) { toast.error("Enter a valid email address"); return; }
    if ((digestCfg.recipients || []).includes(e)) { toast.error("Already added"); setNewRecipient(""); return; }
    saveDigest({ enabled: digestCfg.enabled, weekday: digestCfg.weekday, recipients: [...(digestCfg.recipients || []), e] });
    setNewRecipient("");
  };
  const removeRecipient = (e) => {
    saveDigest({ enabled: digestCfg.enabled, weekday: digestCfg.weekday, recipients: (digestCfg.recipients || []).filter((x) => x !== e) });
  };

  const changePassword = async () => {
    if (cp.next.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (cp.next !== cp.confirm) { toast.error("New passwords do not match"); return; }
    setCpSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: cp.current, new_password: cp.next });
      toast.success("Password changed successfully");
      setCp({ current: "", next: "", confirm: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Could not change password"); }
    finally { setCpSaving(false); }
  };

  return (
    <DashboardLayout title="Settings">
      {!info ? <p className="text-[#8B9BB4]">Loading…</p> : (
        <div className="fade-up max-w-2xl space-y-6">
          <div className="iw-card p-6">
            <p className="overline flex items-center gap-2"><Mail size={14} /> Alert Sender Email</p>
            <p className="text-sm text-[#8B9BB4] mt-1 mb-4">The "from" address used for webhook-failure and system alert emails. Use an address on a domain you've verified in Resend so emails reach any inbox.</p>
            <div className="flex items-center gap-2 mb-3">
              <span className="text-sm text-[#8B9BB4]">Email provider:</span>
              {info.provider_active
                ? <span className="text-green-400 flex items-center gap-1.5 text-sm"><CheckCircle2 size={15} /> Resend connected</span>
                : <span className="text-[#D4AF37] text-sm">Not configured (emails logged only)</span>}
            </div>
            <div className="flex gap-2">
              <input className="iw-input" placeholder="alerts@yourdomain.com" value={sender} onChange={(e) => setSender(e.target.value)} data-testid="sender-email-input" />
              <button onClick={save} disabled={saving} className="btn-gold px-5 rounded-lg text-sm shrink-0 flex items-center gap-2" data-testid="save-sender-btn">{saving ? <Loader2 size={15} className="animate-spin" /> : null} Save</button>
            </div>
            <div className="mt-3 flex items-center gap-3">
              {usingCustom
                ? <span className="text-xs text-green-400 flex items-center gap-1.5"><CheckCircle2 size={13} /> Using your custom sender</span>
                : <span className="text-xs text-[#8B9BB4]">Using Resend's shared test address (delivers only to your verified Resend email)</span>}
              <button onClick={sendTest} disabled={testing} className="btn-ghost px-4 py-2 rounded-full text-xs flex items-center gap-2 ml-auto" data-testid="send-test-email-btn">{testing ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Send test email</button>
            </div>
          </div>

          <div className="iw-card p-6" data-testid="change-password-card">
            <p className="overline flex items-center gap-2"><KeyRound size={14} /> Change Admin Password</p>
            <p className="text-sm text-[#8B9BB4] mt-1 mb-4">Set a strong password for your admin account. Minimum 8 characters.</p>
            <div className="space-y-3 max-w-md">
              <input type="password" className="iw-input" placeholder="Current password" value={cp.current}
                onChange={(e) => setCp((p) => ({ ...p, current: e.target.value }))} data-testid="cp-current" autoComplete="current-password" />
              <input type="password" className="iw-input" placeholder="New password (min 8 chars)" value={cp.next}
                onChange={(e) => setCp((p) => ({ ...p, next: e.target.value }))} data-testid="cp-new" autoComplete="new-password" />
              <input type="password" className="iw-input" placeholder="Confirm new password" value={cp.confirm}
                onChange={(e) => setCp((p) => ({ ...p, confirm: e.target.value }))} data-testid="cp-confirm" autoComplete="new-password" />
              <button onClick={changePassword} disabled={cpSaving || !cp.current || !cp.next} className="btn-gold px-5 py-2.5 rounded-full text-sm inline-flex items-center gap-2 disabled:opacity-60" data-testid="cp-submit">
                {cpSaving ? <Loader2 size={15} className="animate-spin" /> : <KeyRound size={15} />} Update password
              </button>
            </div>
          </div>

          <div className="iw-card p-6" data-testid="digest-card">
            <p className="overline flex items-center gap-2"><BarChart3 size={14} /> Weekly Activity Digest</p>
            <p className="text-sm text-[#8B9BB4] mt-1 mb-4">Admins automatically receive a weekly summary of card views, contact saves, leads, and total cards. Choose when it sends, or send yourself a preview now.</p>

            {digestCfg && (
              <div className="rounded-xl border border-[#ffffff1a] p-4 mb-4 space-y-4" data-testid="digest-schedule">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm text-[#F6F1E4] font-semibold">Weekly digest email</p>
                    <p className="text-xs text-[#8B9BB4] mt-0.5">{digestCfg.enabled ? `Sends every ${digestCfg.weekdays[digestCfg.weekday]}` : "Currently paused"}</p>
                  </div>
                  <button
                    onClick={() => saveDigest({ enabled: !digestCfg.enabled, weekday: digestCfg.weekday })}
                    disabled={savingDigest}
                    data-testid="digest-toggle"
                    className="relative h-7 w-14 rounded-full transition-colors shrink-0"
                    style={{ background: digestCfg.enabled ? "#D4AF37" : "#142B5A" }}>
                    <span className="absolute top-1 h-5 w-5 rounded-full bg-white transition-all" style={{ left: digestCfg.enabled ? "34px" : "4px" }} />
                  </button>
                </div>
                <div className={digestCfg.enabled ? "" : "opacity-40 pointer-events-none"}>
                  <label className="text-xs text-[#8B9BB4] font-semibold mb-1.5 block">Send on</label>
                  <select className="iw-input" value={digestCfg.weekday} data-testid="digest-weekday" disabled={!digestCfg.enabled}
                    onChange={(e) => saveDigest({ enabled: digestCfg.enabled, weekday: parseInt(e.target.value, 10) })}>
                    {digestCfg.weekdays.map((d, i) => <option key={d} value={i}>{d}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-[#8B9BB4] font-semibold mb-1.5 block">Extra recipients</label>
                  <p className="text-xs text-[#8B9BB4] mb-2">Add teammates (e.g. a manager) who should also get the weekly digest.</p>
                  <div className="flex gap-2">
                    <input className="iw-input" placeholder="manager@company.com" value={newRecipient}
                      onChange={(e) => setNewRecipient(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addRecipient(); } }}
                      data-testid="digest-recipient-input" />
                    <button onClick={addRecipient} disabled={savingDigest} className="btn-gold px-4 rounded-lg text-sm shrink-0" data-testid="digest-recipient-add">Add</button>
                  </div>
                  {(digestCfg.recipients || []).length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3" data-testid="digest-recipient-list">
                      {digestCfg.recipients.map((e) => (
                        <span key={e} className="inline-flex items-center gap-1.5 text-xs bg-[#142B5A] text-[#F6F1E4] px-2.5 py-1.5 rounded-full" data-testid={`digest-recipient-${e}`}>
                          {e}
                          <button onClick={() => removeRecipient(e)} className="text-[#8B9BB4] hover:text-red-400 transition-colors" data-testid={`digest-recipient-remove-${e}`}><X size={12} /></button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            <button onClick={previewDigest} disabled={digestLoading} className="btn-gold px-5 py-2.5 rounded-full text-sm inline-flex items-center gap-2" data-testid="preview-digest-btn">
              {digestLoading ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Send me a preview now
            </button>
            {digestHtml && (
              <div className="mt-5 rounded-xl border border-[#ffffff1a] p-4 bg-[#081426]" data-testid="digest-preview">
                <p className="overline mb-2">Preview</p>
                <div className="text-sm text-[#c9d3e0] leading-relaxed" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(digestHtml) }} />
              </div>
            )}
          </div>

          <div className="iw-card p-6">
            <p className="overline flex items-center gap-2"><Globe size={14} /> Verify a sending domain in Resend</p>
            <p className="text-sm text-[#8B9BB4] mt-1 mb-4">To email any recipient (not just your own verified address), verify a domain in Resend and add its DNS records:</p>
            <ol className="space-y-3 text-sm text-[#8B9BB4]">
              {[
                ["Open Resend → Domains", "Go to resend.com/domains and click \u201CAdd Domain\u201D, then enter a domain you control (e.g. yourbrand.com)."],
                ["Add the DNS records", "Resend shows SPF, DKIM and (optional) DMARC records — add them at your DNS host (GoDaddy, Cloudflare, etc.)."],
                ["Wait for verification", "Resend re-checks DNS automatically; status turns to Verified (usually minutes to a few hours)."],
                ["Set your sender here", "Come back and set the sender above to something like alerts@yourbrand.com, then Save and send a test."],
              ].map(([t, d], i) => (
                <li key={t} className="flex gap-3">
                  <span className="font-display text-lg font-bold text-[#D4AF37] leading-none">{i + 1}</span>
                  <span><span className="text-[#F6F1E4] font-semibold">{t}. </span>{d}</span>
                </li>
              ))}
            </ol>
            <a href="https://resend.com/domains" target="_blank" rel="noreferrer" className="btn-gold inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm mt-5" data-testid="open-resend-btn"><Globe size={15} /> Open Resend Domains</a>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
